import { config } from '../../config';
import type { RecorderAction, RecordingSubject } from '../../app/recorder';

/**
 * B-11 — the bottom band's **pure decisions** (design spec §2.4; scenarios 9, 13, 19).
 * DOM-free so the Node suite drives every arm (§12's grammar); the band component is glue
 * over these plus the engine's own predicates (`pickUndoTarget`, `msUntilUndoCloses`,
 * `isInMatch` — imported there, never re-derived here).
 */

/** The shape the undo pill needs from a hypothesis row — the same three fields every capture
 *  surface already holds (`DiscoveryHypothesisView` and `BoardTileView` both satisfy it). */
export interface UndoRowB {
  readonly id: string;
  readonly text: string;
  readonly lastIncrementTs: number | null;
}

/**
 * Scenario 9 — the pill's **visible countdown**, as arithmetic the stylesheet animates.
 *
 * The truth of the window stays the engine's (`undoOpen` / `pickUndoTarget` over the committed
 * `lastIncrementTs` — §5.4's "no timer as truth"); what this computes is only how much of it is
 * left to *draw* at the moment the pill mounts. The CSS animation runs `remainingMs` from
 * `fraction` down to zero, so no polling exists: one number at mount, one one-shot re-eval when
 * the window closes (`msUntilUndoCloses`, armed by the band).
 *
 * Returns `null` when there is nothing to draw — no increment, or a window already spent.
 * A `lastIncrementTs` in the future clamps to the full window (the same forward-clock reading
 * `undoOpen` chose: open rather than silently closed).
 */
export interface UndoCountdown {
  readonly remainingMs: number;
  /** 0..1 — how much of the window remains, for the bar's starting scale. */
  readonly fraction: number;
}

export function undoCountdown(lastIncrementTs: number | null, now: number): UndoCountdown | null {
  if (lastIncrementTs === null || !Number.isFinite(lastIncrementTs) || !Number.isFinite(now)) {
    return null;
  }
  const windowMs = config.UNDO_WINDOW_S * 1000;
  const remainingMs = Math.min(windowMs, lastIncrementTs + windowMs - now);
  if (remainingMs <= 0) return null;
  return { remainingMs, fraction: remainingMs / windowMs };
}

/**
 * FLAG-4 / §6.4a — what the band says about a flag press, from the recorder's own verdict.
 *
 * `null` = nothing to say: a live recording announces itself through the chip (driven by
 * `view.recording`, which only a REAL recording populates — a bug that lit the chip would have
 * to invent one), and a stop's acknowledgment is the chip disappearing. The two arms that DO
 * speak are the ones with nothing else to show: a refusal (before kickoff there is no minute to
 * stamp — §6.4a's negative, said where the finger is) and the silent flag (the flag is real and
 * correctly timed; only the audio is missing — FLAG-4 requires the press never be silent to the
 * user even when the recording is).
 */
export function flagNoticeFor(action: RecorderAction): string | null {
  switch (action.kind) {
    case 'refused':
      return 'Not recording — the match has not kicked off.';
    case 'started':
      return action.handle.silent
        ? `Silent flag saved at ${String(action.handle.matchMinute)}′ — no microphone.`
        : null;
    case 'stopped':
      return null;
  }
}

/**
 * FLAG-1 — *"recording state is visible"*, named for what is being recorded. A `switch` over
 * the union so a fourth subject is a compile error here rather than a silent mislabel (the A
 * flag bar's reasoning, which is the engine-adjacent shape both bars must agree with).
 */
export function recordingCaption(kind: RecordingSubject['kind']): string {
  switch (kind) {
    case 'flag':
      return 'flag';
    case 'voiceNote':
      return 'note';
    case 'matchNote':
      return 'halftime note';
  }
}

/**
 * The shell's undo-row book-keeping: fold a surface's published rows into what it already
 * holds, **by id, never wholesale**.
 *
 * Why a merge: the locked board publishes only its tiles, but list-only rows exist on a locked
 * match (Discovery adds) and can hold an open window — the user visits Discovery, taps a
 * list-only row, returns to the board inside `UNDO_WINDOW_S`. A wholesale replace would drop
 * that row from the pill's candidates while its window is still open; the merge keeps every row
 * ever seen and lets each publisher refresh the ones it renders. Rows are never deleted
 * mid-match (a cascade takes the whole match, and the shell navigates away), so retention
 * cannot resurrect anything.
 */
export function mergeUndoRows(
  previous: readonly UndoRowB[],
  published: readonly UndoRowB[],
): readonly UndoRowB[] {
  const merged = new Map<string, UndoRowB>();
  for (const row of previous) merged.set(row.id, row);
  for (const row of published) {
    merged.set(row.id, { id: row.id, text: row.text, lastIncrementTs: row.lastIncrementTs });
  }
  return [...merged.values()];
}
